import { useEffect, useState } from 'react'
import {
  IcePackPart,
  InventoryStatus,
  InventoryTemperatureSensorTag,
} from '../../types/typescript-axios'
import { DefaultWithTitleLayout } from '../../components/templates/default-with-title-layout'
import { VixellTable } from '../../components/part/vixell-table'
import { TableElementDatetime2Line } from '../../components/part/table-elements/table-element-datetime-2-line'
import moment from 'moment'
import { AlreadyIcon } from '../../components/molecules/already-icon'
import { NotYetIcon } from '../../components/molecules/not-yet-icon'
import { TableElementEpc } from '../../components/part/table-elements/table-element-epc'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SearchInput } from '../../components/molecules/search-input'
import { Select } from '../../components/molecules/select'
import { Lang } from '../../translations/lang'
import { downloadCsv } from '../../utils/download-csv'
import { toFormattedDatetime } from '../../utils/to-formatted-datetime'
import { webRequest } from '../../types/handle-api'
import { usePaging } from '../../contexts/use-paging'

const InventoryStates = [
  [Lang.all, ''] as const,
  [Lang.inventoryState.notYet, 'not-yet'] as const,
]

const IcePackParts = [
  [Lang.all, ''] as const,
  [Lang.icePack.TOP, 'TOP'] as const,
  [Lang.icePack.BOTTOM, 'BOTTOM'] as const,
  [Lang.icePack.FRONT, 'FRONT'] as const,
  [Lang.icePack.BACK, 'BACK'] as const,
  [Lang.icePack.LEFT, 'LEFT'] as const,
  [Lang.icePack.RIGHT, 'RIGHT'] as const,
]

export const IcePackInventories = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [inventories, setInventories] = useState<
    InventoryTemperatureSensorTag[]
  >([])

  const [searchWord, setSearchWord] = useState<string>('')
  const [searchPart, setSearchPart] = useState<string>('')
  const [searchInventoryState, setSearchInventoryState] = useState<string>('')
  const [searchFrom, setSearchFrom] = useState<string>('')
  const [searchTill, setSearchTill] = useState<string>('')

  const [loadNextPageAt, setLoadNextPageAt] = useState<Date>(new Date())
  const [searchParamsString, setSearchParamsString] = useState<string>()
  const { currentPage, stopPageLoad, setPaging } = usePaging()

  useEffect(() => {
    fetchDataAndSetStates()
  }, [searchParams])

  async function fetchDataAndSetStates(loadNextPage = false) {
    const response = await fetchInventories({ loadNextPage })
    if (response.status === 200) {
      if (searchParams.toString() === searchParamsString) {
        setInventories((i) => [...i, ...response.data.temperatureSensorTags])
      } else {
        setInventories(response.data.temperatureSensorTags)
      }
      setPaging(response.data)
      setSearchParamsString(searchParams.toString())
    }
  }

  async function fetchInventories({ all = false, loadNextPage = false }) {
    const query = searchParams.get('search')
    const part = searchParams.get('part')
    const state = searchParams.get('state')
    const from = searchParams.get('from')
    const till = searchParams.get('till')

    setSearchWord((w) => w || query || '')
    setSearchPart(part || '')
    setSearchInventoryState(state || '')
    setSearchFrom(from || '')
    setSearchTill(till || '')

    return await webRequest('InventoriesTemperatureSensorTagsIndex', {
      ...(query && { query }),
      ...(part && { part: part as IcePackPart }),
      ...(state && { status: state as InventoryStatus }),
      ...(from && { from }),
      ...(till && { till }),
      ...(all && { all: true }),
      ...(!all && loadNextPage && { page: currentPage + 1 }),
    })
  }

  async function csvDownload() {
    const response = await fetchInventories({ all: true })
    if (response.status === 200) {
      const csvData = [
        [
          Lang.icePackSerialNo,
          Lang.belongedSite,
          Lang.contractRepresentativeNo,
          Lang.contractNo,
          Lang.managementNo,
          Lang.vixellInventories.inventoryState,
          Lang.inspectionTime,
        ],
        ...response.data.temperatureSensorTags.map(
          ({
            temperatureSensorTagEpc,
            siteName,
            processingDateTime,
            contractRepresentativeNo,
          }) => [
            temperatureSensorTagEpc,
            siteName,
            contractRepresentativeNo,
            Lang.icePack['LEFT'], // TODO:
            moment(processingDateTime).isAfter(moment().subtract(3, 'months'))
              ? Lang.already
              : Lang.notYet,
            toFormattedDatetime(processingDateTime),
          ]
        ),
      ]
      downloadCsv(csvData, `icePackInventories-${new Date().toISOString()}.csv`)
    }
  }

  function infiniteLoad() {
    if (stopPageLoad) return
    if (moment(loadNextPageAt).isAfter(moment().subtract(3, 'seconds'))) return

    setLoadNextPageAt(new Date())
    fetchDataAndSetStates(true)
  }

  function changeSearchParams(label: string, value: string) {
    if (value) searchParams.set(label, value)
    if (!value) searchParams.delete(label)
    navigate(`/inventories/ice-packs?${searchParams.toString()}`)
  }

  const titleRightElement = (
    <div className="flex flex-col items-end">
      <div>
        <SearchInput
          value={searchWord}
          onChange={setSearchWord}
          onClick={() => changeSearchParams('search', searchWord)}
        />
      </div>
      <div className="flex items-end mt-16 gap-[48px]">
        <div className="flex flex-col">
          <label>{Lang.icePackPart}</label>
          <Select
            className="mt-8"
            value={searchPart}
            options={IcePackParts}
            onChange={(value) => changeSearchParams('part', value)}
          />
        </div>
        <div className="flex flex-col">
          <label>{Lang.vixellInventories.inventoryState}</label>
          <Select
            className="mt-8"
            value={searchInventoryState}
            options={InventoryStates}
            onChange={(value) => changeSearchParams('state', value)}
          />
        </div>
        <div>
          <label>{Lang.lastInspectionTime}</label>
          <div className="flex items-center gap-[12px]">
            <input
              type="date"
              className="w-[160px]"
              value={searchFrom}
              onChange={(e) => changeSearchParams('from', e.target.value)}
            />
            <span className="inline-block h-[36px] text-3xl text-gray-300">
              〜
            </span>
            <input
              type="date"
              className="w-[160px]"
              value={searchTill}
              onChange={(e) => changeSearchParams('till', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <DefaultWithTitleLayout
      title={Lang.icePackInventories.title}
      description={Lang.icePackInventories.description}
      titleRightElement={titleRightElement}
      infiniteLoad={infiniteLoad}
      csvDownload={csvDownload}
    >
      {inventories && (
        <VixellTable
          rootClassName="my-64"
          classNames={['w-200', 'w-200', 'w-200', 'w-200', 'w-200']}
          headers={[
            Lang.icePackSerialNo,
            Lang.belongedSite,
            Lang.contractRepresentativeNo,
            Lang.icePackPart,
            Lang.vixellInventories.inventoryState,
            Lang.inspectionTime,
          ]}
          elements={inventories.map((inventory, index) => [
            <TableElementEpc
              epc={inventory.temperatureSensorTagEpc}
              key={index}
            />,
            inventory.siteName,
            inventory.contractRepresentativeNo,
            Lang.icePack[inventory.part],
            inventory.settled ? <AlreadyIcon /> : <NotYetIcon />,
            inventory.processingDateTime && (
              <TableElementDatetime2Line
                dateTime={inventory.processingDateTime}
              />
            ),
          ])}
        />
      )}
    </DefaultWithTitleLayout>
  )
}
